import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { throwEdgeFunctionError } from '@/lib/edgeFunctionError';
import type { Database } from '@/types/database';
import type { TrainingBalance } from '@/types';

type BillingRow = Database['public']['Tables']['user_billing']['Row'];
const BASE_PLAN_PRICE = '$39/month';
const INCLUDED_TRAINING_HOURS = 3;
const ADDITIONAL_HOUR_PRICE = '$10';
const fallbackTrainingBalance: TrainingBalance = {
  subscriptionStatus: 'inactive',
  includedHours: 0,
  grantedHours: 0,
  usedHours: 0,
  remainingHours: 0,
  hasAccess: false,
  monthEndsAt: null,
};

const statusDescriptions: Record<string, string> = {
  inactive: 'You do not have an active subscription.',
  active: 'Your subscription is active.',
  trialing: 'You are in a trial period.',
  past_due: 'Payment failed — update your payment method in the billing portal.',
  canceled: 'This subscription has been canceled.',
  unpaid: 'This subscription has unpaid invoices.',
  incomplete: 'Checkout was started but not finished.',
  incomplete_expired: 'The incomplete checkout expired.',
  paused: 'Your subscription is paused.',
};

function formatRenewal(end: string | null): string | null {
  if (!end) return null;
  try {
    return new Date(end).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

const Billing: React.FC<{ trainingBalance?: TrainingBalance }> = ({ trainingBalance }) => {
  const { user } = useAuth();
  const effectiveTrainingBalance = trainingBalance ?? fallbackTrainingBalance;
  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingRow | null | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'checkout' | 'portal' | 'hours' | null>(null);
  const [hourQuantity, setHourQuantity] = useState(1);

  const checkoutSuccess = searchParams.get('checkout') === 'success';
  const hourPurchaseSuccess = searchParams.get('hours') === 'success';
  const billingRequired = searchParams.get('billingRequired') === '1';

  const dismissSuccess = useCallback(() => {
    searchParams.delete('checkout');
    searchParams.delete('hours');
    searchParams.delete('billingRequired');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!supabase || !user) {
        if (!cancelled) setBilling(null);
        return;
      }
      const { data, error } = await supabase.from('user_billing').select('*').eq('user_id', user.id).maybeSingle();
      if (cancelled) return;
      if (error) {
        setLoadError(error.message);
        setBilling(undefined);
        return;
      }
      setLoadError(null);
      setBilling(data ?? null);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const activeLike = useMemo(() => {
    const s = billing?.subscription_status;
    return s === 'active' || s === 'trialing';
  }, [billing?.subscription_status]);

  const subscribe = async () => {
    if (!supabase) return;
    setActionError(null);
    setBusy('checkout');
    try {
      const { data, error: fnError } = await supabase.functions.invoke<{ url?: string }>(
        'stripe-create-checkout-session',
        { method: 'POST', body: {} },
      );
      if (fnError) await throwEdgeFunctionError(fnError);
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const openPortal = async () => {
    if (!supabase) return;
    setActionError(null);
    setBusy('portal');
    try {
      const { data, error: fnError } = await supabase.functions.invoke<{ url?: string }>(
        'stripe-customer-portal',
        { method: 'POST', body: {} },
      );
      if (fnError) await throwEdgeFunctionError(fnError);
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error('No portal URL returned');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const purchaseHours = async () => {
    if (!supabase) return;
    setActionError(null);
    setBusy('hours');
    try {
      const { data, error: fnError } = await supabase.functions.invoke<{ url?: string }>(
        'stripe-create-hours-checkout-session',
        { method: 'POST', body: { hours: hourQuantity } },
      );
      if (fnError) await throwEdgeFunctionError(fnError);
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error('No checkout URL returned');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (!supabase) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-2">Billing & subscription</h2>
        <p className="text-[#555555] text-sm">Connect Supabase in your environment to manage subscriptions.</p>
      </div>
    );
  }

  const statusLabel = billing?.subscription_status ?? 'inactive';
  const blurb =
    statusDescriptions[statusLabel] ?? `Subscription status: ${statusLabel.replace(/_/g, ' ')}.`;
  const renewal = formatRenewal(billing?.current_period_end ?? null);

  return (
    <div className="space-y-4">
      {checkoutSuccess && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex flex-wrap items-center justify-between gap-2"
        >
          <span>Thanks — we are confirming your subscription. This usually takes a few seconds.</span>
          <button
            type="button"
            onClick={() => dismissSuccess()}
            className="font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {hourPurchaseSuccess && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex flex-wrap items-center justify-between gap-2"
        >
          <span>Your training hours purchase was successful and has been applied to this month.</span>
          <button
            type="button"
            onClick={() => dismissSuccess()}
            className="font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}
      {billingRequired && !checkoutSuccess && (
        <div
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2"
        >
          <span>Training access requires either an active subscription or available training hours.</span>
          <button
            type="button"
            onClick={() => dismissSuccess()}
            className="font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">{loadError}</div>
      )}
      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">{actionError}</div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-[#ECECEC]">
        <h2 className="text-xl font-semibold font-poppins text-[#1A1A1A] mb-2">Billing & subscription</h2>
        <p className="text-[#555555] text-sm mb-6">Subscribe with Stripe or manage payment methods and invoices.</p>
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-[#F5F5F5] p-3 text-[#121212]">
            <CreditCard size={28} />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="text-lg font-semibold font-poppins text-[#1A1A1A]">Kipr plan</h3>
            <ul className="text-sm text-[#555555] space-y-1">
              <li>{BASE_PLAN_PRICE} includes {INCLUDED_TRAINING_HOURS} hours of training each month.</li>
              <li>Additional training time is {ADDITIONAL_HOUR_PRICE} per hour.</li>
              <li>Unused hours expire at the end of each billing month.</li>
            </ul>
            <p className="text-[#555555] text-sm">{blurb}</p>
            <p className="text-sm text-[#1A1A1A]">
              <span className="font-semibold">{effectiveTrainingBalance.remainingHours.toFixed(2)} hours</span>{' '}
              remaining this month ({effectiveTrainingBalance.usedHours.toFixed(2)} used)
            </p>
            <div className="text-sm text-[#777777]">
              {billing === undefined ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="animate-spin" size={16} />
                  Loading billing…
                </span>
              ) : (
                <>
                  <span className="font-medium text-[#1A1A1A] capitalize">{statusLabel.replace(/_/g, ' ')}</span>
                  {activeLike && renewal && (
                    <span className="block mt-1">Renews on {renewal}</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => void subscribe()}
            disabled={busy !== null || billing === undefined || activeLike}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#121212] px-4 py-2.5 text-sm font-medium text-white hover:bg-black/90 disabled:opacity-45 disabled:pointer-events-none min-w-[160px]"
          >
            {busy === 'checkout' ? <Loader2 className="animate-spin" size={18} /> : null}
            {activeLike ? 'Current plan active' : 'Subscribe'}
          </button>
          <button
            type="button"
            onClick={() => void openPortal()}
            disabled={busy !== null || billing === undefined || !billing?.stripe_customer_id}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#DADADA] bg-white px-4 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] disabled:opacity-45 disabled:pointer-events-none"
          >
            {busy === 'portal' ? <Loader2 className="animate-spin" size={18} /> : null}
            Manage billing portal
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label htmlFor="extra-hours" className="text-sm text-[#555555]">
            Add hours:
          </label>
          <input
            id="extra-hours"
            type="number"
            min={1}
            max={100}
            step={1}
            value={hourQuantity}
            onChange={(event) => {
              const parsed = Number(event.target.value);
              setHourQuantity(Number.isFinite(parsed) ? Math.max(1, Math.min(100, Math.floor(parsed))) : 1);
            }}
            className="w-24 rounded-md border border-[#DADADA] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#121212]/20"
          />
          <button
            type="button"
            onClick={() => void purchaseHours()}
            disabled={busy !== null || billing === undefined}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-[#DADADA] bg-white px-4 py-2 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] disabled:opacity-45 disabled:pointer-events-none"
          >
            {busy === 'hours' ? <Loader2 className="animate-spin" size={16} /> : null}
            Buy at $10/hour
          </button>
        </div>
      </div>
    </div>
  );
};

export default Billing;
